import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import type { Badges } from '@tg/shared';
import { paths } from '@/app/paths';
import { useLocale } from '@/i18n/useLocale';
import { useSocketEvent } from '@/socket/socket';
import { useBadges } from '@/stores/badges';
import { selectInGame, useScreen } from '@/stores/screen';
import { useSession } from '@/stores/session';
import { useToasts } from '@/stores/toasts';
import { socialApi } from './api';

/** a friend-request toast: long enough to reach the link; in a game — short (it must not linger over the round) */
export const REQUEST_TOAST_MS = 15_000;
export const REQUEST_TOAST_GAME_MS = 6_000;
/** after the UTC midnight the daily bonus is back: refetch a little later (the server clock may be a bit behind) */
export const MIDNIGHT_SLACK_MS = 5_000;

/** ms until the next UTC midnight (+ slack) */
export function untilUtcMidnight(now = Date.now()): number {
  const d = new Date(now);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1) - now + MIDNIGHT_SLACK_MS;
}

/**
 * Menu counters and the friend-request toast (spec 2026-09-30 §1).
 * - signed in → GET /api/me/badges; afterwards the `badges` socket event (also sent by the server on every connect);
 *   signed out → cleared;
 * - changes the server does not push: leaving a game screen (a solo round may stay unfinished → «Games») and the UTC
 *   midnight (the daily bonus is back) → GET again;
 * - `friends:request` → «New friend request: {name}» with a link to the friends page, unless the player is already
 *   there. Toasts never make a sound; during a game the toast is short and stays in the top corner, away from the bet
 *   controls.
 */
export function BadgesListener() {
  const status = useSession((st) => st.status);
  const l = useLocale();
  const { pathname } = useLocation();
  const pushed = useRef(0);

  const refresh = useCallback((signal?: AbortSignal) => {
    const seen = pushed.current;
    socialApi.badges({ signal }).then(
      (b) => {
        // a socket push after the request started is newer than this answer
        if (pushed.current === seen) useBadges.getState().set(b);
      },
      () => undefined,
    );
  }, []);

  useEffect(() => {
    if (status !== 'user') {
      useBadges.getState().clear();
      return;
    }
    const ac = new AbortController();
    refresh(ac.signal);
    return () => ac.abort();
  }, [status, refresh]);

  // back from a game screen: a solo round may have been left unfinished
  const inGame = useScreen(selectInGame);
  const wasInGame = useRef(inGame);
  useEffect(() => {
    if (wasInGame.current && !inGame && useSession.getState().status === 'user') refresh();
    wasInGame.current = inGame;
  }, [inGame, refresh]);

  // the daily bonus was claimed today: it is back after the UTC midnight
  const dailyTaken = useBadges((st) => st.badges?.dailyBonus === false);
  useEffect(() => {
    if (status !== 'user' || !dailyTaken) return;
    const t = setTimeout(() => refresh(), untilUtcMidnight());
    return () => clearTimeout(t);
  }, [status, dailyTaken, refresh]);

  useSocketEvent<[Badges]>('badges', (b) => {
    if (useSession.getState().status !== 'user') return;
    pushed.current += 1;
    useBadges.getState().set(b);
  });

  useSocketEvent<[{ publicId: number; nickname: string }]>('friends:request', (e) => {
    if (useSession.getState().status !== 'user') return;
    const friends = paths.friends(l);
    if (pathname === friends || pathname.startsWith(`${friends}/`)) return;
    const inGame = selectInGame(useScreen.getState());
    useToasts.getState().push({
      kind: 'notice',
      titleId: 'social.badges.requestToast',
      values: { name: e.nickname },
      link: { to: friends, labelId: 'social.badges.requestToastLink' },
      ttl: inGame ? REQUEST_TOAST_GAME_MS : REQUEST_TOAST_MS,
    });
  });

  return null;
}
